import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, Strippenkaart } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Ticket, ShoppingCart, LogOut, Users, Info, ChevronDown, ChevronUp, CreditCard, CheckCircle, Euro, Clock, Infinity, Star, Check, MessageCircle, Calendar, MessageSquare } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '@/assets/logo.webp';

export default function MijnStrippenkaart() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [strippenkaarten, setStrippenkaarten] = useState<Strippenkaart[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [registeredEvents, setRegisteredEvents] = useState<Array<{
    eventId: string;
    eventDatum: string;
    inschrijfDatum: string;
  }>>([]);

  const loadRegisteredEvents = async () => {
    if (!user) {
      setRegisteredEvents([]);
      return;
    }

    const { data, error } = await supabase
      .from('event_registrations')
      .select('event_id, event_datum, aangemeld_op')
      .eq('user_id', user.id)
      .eq('status', 'bevestigd')
      .order('aangemeld_op', { ascending: false });

    if (error) {
      console.error('Error loading registered events:', error);
      return;
    }

    if (data) {
      const events = data.map(reg => ({
        eventId: reg.event_id,
        eventDatum: reg.event_datum,
        inschrijfDatum: reg.aangemeld_op
      }));
      setRegisteredEvents(events);
    }
  };

  useEffect(() => {
    loadStrippenkaarten();
    loadRegisteredEvents();
  }, [user]);

  // Handle success/cancel from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast.success('Betaling succesvol! Je strippenkaart wordt toegevoegd...');
      // Refresh de strippenkaarten na een korte delay om webhook tijd te geven
      setTimeout(() => {
        loadStrippenkaarten();
      }, 2000);
      // Clean URL
      window.history.replaceState({}, '', '/mijn-strippenkaart');
    }
    if (urlParams.get('canceled') === 'true') {
      toast.info('Betaling geannuleerd');
      window.history.replaceState({}, '', '/mijn-strippenkaart');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStrippenkaarten = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('strippenkaarten')
      .select('*')
      .eq('user_id', user.id)
      .order('gekocht_op', { ascending: false });

    if (error) {
      console.error('Error loading strippenkaarten:', error);
      toast.error('Fout bij laden van strippenkaarten');
    } else {
      setStrippenkaarten(data || []);
    }
    setLoading(false);
  };

  const handleBuyStrippenkaart = async () => {
    if (!user) {
      toast.error('Je moet ingelogd zijn om een strippenkaart te kopen');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
          mode: 'account',
        }),
      });

      const data = await response.json();
      
      if (!response.ok || data.error) {
        throw new Error(data.error || 'Fout bij aanmaken van betalingslink');
      }
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Geen checkout URL ontvangen');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Fout bij aanmaken van betalingslink. Probeer het opnieuw.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    toast.success('Je bent uitgelogd');
    navigate('/');
  };

  const totalStrippen = strippenkaarten.reduce((sum, s) => sum + s.resterende_strippen, 0);
  const activeStrippenkaart = strippenkaarten.find(s => s.resterende_strippen > 0) || strippenkaarten[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9EBEF]">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-7xl mx-auto">
          
          {/* Linker Kolom: Jouw Saldo */}
          <div className="space-y-6">
            {/* Titel los boven de kaart */}
            <h2 className="font-serif text-3xl md:text-4xl text-[#854C73]">
              Jouw Saldo
            </h2>

            {activeStrippenkaart && (
              <>
                {/* Saldo Kaart */}
                <div className="group bg-white rounded-[2rem] border border-gray-100 shadow-xl p-10 relative overflow-hidden">
                  <div className="absolute -top-10 -right-8 w-20 h-20 bg-[#F6E6EE] rounded-full opacity-80 pointer-events-none transition-all duration-500 ease-out group-hover:w-32 group-hover:h-32 group-hover:translate-x-3 group-hover:-translate-y-2" />

                  <div className="relative z-10 space-y-6">
                    {/* Header + Teller */}
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-[#864F87] mb-1">
                          ACTIEVE KAART
                        </div>
                        <p className="font-serif text-xl text-[#6b4912]">
                          Gekocht op{' '}
                          {new Date(activeStrippenkaart.gekocht_op).toLocaleDateString('nl-NL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })}
                        </p>
                      </div>

                      <div className="text-right">
                        <div className="font-serif text-6xl font-light text-[#854C73] leading-none">
                          {activeStrippenkaart.resterende_strippen}
                        </div>
                        <div className="text-xs font-bold text-[#854C73] uppercase tracking-[0.2em] mt-2">
                          STRIPPEN OVER
                        </div>
                      </div>
                    </div>

                    {/* Strippen Visualisatie */}
                    <div className="mt-4 rounded-2xl border border-[#F1E4EC] bg-[#FBF5F7] p-6">
                      <div className="flex justify-center gap-6">
                        {Array.from({ length: activeStrippenkaart.totaal_strippen }).map((_, index) => {
                          const isUsed = index >= activeStrippenkaart.resterende_strippen;
                          return (
                            <div key={index} className="flex flex-col items-center">
                              <div className="relative">
                                <div
                                  className={`w-16 h-16 rounded-full flex items-center justify-center border-2 relative transition-colors overflow-hidden ${
                                    isUsed
                                      ? 'bg-gray-50 border-gray-200'
                                      : 'bg-white border-[#854C73]'
                                  }`}
                                >
                                  <img 
                                    src={logo} 
                                    alt="Systemique logo stempel - Strippenkaart validatie" 
                                    className={`h-8 w-8 object-contain transition-all ${
                                      isUsed 
                                        ? 'opacity-30 grayscale rotate-[8deg]' 
                                        : 'opacity-100 rotate-[-8deg]'
                                    }`}
                                  />

                                  <div
                                    className={`absolute -top-2 -left-2 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                      isUsed ? 'bg-gray-300 text-white' : 'bg-[#854C73] text-white'
                                    }`}
                                  >
                                    {index + 1}
                                  </div>
                                </div>
                              </div>
                              <div className="text-center mt-2">
                                <span
                                  className={`text-[10px] font-bold ${
                                    isUsed ? 'text-gray-400 uppercase' : 'text-[#854C73]'
                                  }`}
                                >
                                  {isUsed ? 'GEBRUIKT' : '1 opstelling'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gereserveerde Evenementen Sectie */}
                {registeredEvents.length > 0 && (
                  <div className="mt-6 rounded-2xl border border-[#F1E4EC] bg-[#FBF5F7] p-6">
                    <h3 className="font-serif text-lg text-[#854C73] mb-4">
                      Gereserveerde Evenementen
                    </h3>
                    <div className="space-y-3">
                      {registeredEvents.map((event, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#F1E4EC]">
                          <div>
                            <p className="font-semibold text-[#854C73] text-sm">
                              Opstellingenmiddag - {event.eventDatum}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Ingeschreven op {new Date(event.inschrijfDatum).toLocaleDateString('nl-NL', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                          <Badge className="bg-green-100 text-green-700 border-0">
                            Ingeschreven
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* "Goed om te weten" Sectie - Aparte kaart */}
                <div className="bg-white rounded-2xl border border-gray-200 p-8">
                  <h3 className="font-serif text-xl text-[#854C73] mb-6">
                    Goed om te weten
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Item 1: Onbeperkt geldig */}
                    <div className="flex items-start gap-4">
                      <div className="bg-[#FAEBEE] rounded-xl p-3 flex items-center justify-center flex-shrink-0">
                        <Infinity className="h-5 w-5 text-[#854C73]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#854C73] mb-1">Onbeperkt geldig</p>
                        <p className="text-sm text-gray-600">
                          Je strippenkaart verloopt nooit. Gebruik hem wanneer het jou uitkomt.
                        </p>
                      </div>
                    </div>

                    {/* Item 2: Inzetten? */}
                    <div className="flex items-start gap-4">
                      <div className="bg-[#FAEBEE] rounded-xl p-3 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="h-5 w-5 text-[#854C73]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#854C73] mb-1">Inzetten?</p>
                        <p className="text-sm text-gray-600">
                          Bekijk de <Link to="/agenda" className="text-[#854C73] hover:underline">agenda</Link> voor de komende opstellingenmiddagen en ik regel de rest.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!activeStrippenkaart && (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500">
                <p>Je hebt nog geen strippenkaarten.</p>
              </div>
            )}
          </div>

          {/* Rechter Kolom: Besteloverzicht */}
          <div>
            <div className="bg-white rounded-xl shadow-2xl border border-[#854C73]/10 p-6 md:p-8">
              {/* Titel bovenaan */}
              <h2 className="font-serif text-3xl text-[#854C73] mb-6">
                Besteloverzicht
              </h2>

              {/* Product Box */}
              <div className="border border-gray-100 rounded-lg p-6 relative overflow-hidden hover:shadow-md transition-shadow">
                {/* Badge linksboven (schuin) */}
                <span 
                  className="absolute top-2 left-2 bg-[#4ade80] text-white text-xs font-bold px-3 py-1 rounded z-10 uppercase" 
                  style={{ 
                    transform: 'rotate(-15deg)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  Bestel nu
                </span>
                
                <div className="pt-6 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Icoon in zachtpaars vlakje */}
                      <div className="p-2 bg-[#FAEBEE] rounded-lg">
                        <ShoppingCart className="h-5 w-5 text-[#854C73]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-serif text-lg text-[#854C73] font-bold mb-1">
                          3-Strippen Strippenkaart
                        </h4>
                        <p className="text-xs text-gray-600">
                          Geldig voor deelname als representant. Geen vervaldatum.
                        </p>
                      </div>
                    </div>
                    <div className="font-bold text-xl text-[#854C73] whitespace-nowrap">
                      € 130,00
                    </div>
                  </div>
                </div>
              </div>

              {/* Prijs Berekening */}
              <div className="space-y-3 mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between text-sm text-gray-600 border-b border-gray-100 pb-2">
                  <span>Subtotaal</span>
                  <span>€ 107,44</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 border-b border-gray-100 pb-2">
                  <span>BTW (21%)</span>
                  <span>€ 22,56</span>
                </div>
                <div className="flex justify-between font-serif font-bold text-xl pt-2">
                  <span className="text-[#854C73]">Totaalbedrag</span>
                  <span className="text-[#854C73]">€ 130,00</span>
                </div>
              </div>

              {/* E-mail veld en Actie */}
              <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-bold tracking-wider uppercase text-[#6b4912] mb-2">
                    E-MAILADRES VOOR BEVESTIGING
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="w-full px-4 py-2 bg-[#FAEBEE]/30 border border-[#854C73]/10 rounded-lg text-[#6b4912]"
                  />
                </div>

                <Button 
                  onClick={handleBuyStrippenkaart}
                  className="w-full bg-[#854C73] hover:bg-[#854C73] text-white font-serif uppercase tracking-widest h-14 rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Bezig...' : 'NU AFREKENEN'}
                </Button>

                <div className="flex items-center gap-2 text-sm text-gray-600 justify-center">
                  <CheckCircle className="h-4 w-4 text-[#4ade80]" />
                  <span>Betaal met Apple Pay of iDEAL</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

